import { queryOptions } from '@tanstack/react-query'
import { merkl } from '../merkl'
import { Address, formatUnits } from 'viem'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { formatPointsAmount } from '../utils'
import { POINTS_TOKEN_ADDRESS, finishedWeeks } from '@/config/chain/points'

const formatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function userPointsQueryOptions({ account, chainId }: { account: Address; chainId: number }) {
  return queryOptions({
    queryKey: userPointsQueryKey(account),
    queryFn: async () => {
      const userRewards = await merkl
        .users({
          address: account,
        })
        .rewards.get({
          query: {
            chainId: [chainId.toString()],
            test: true,
          },
        })

      const rewardTotalsPromises = finishedWeeks.map(({ rewardTokenAddress }) => {
        return merkl.rewards.token.total.get({
          query: {
            chainId,
            address: rewardTokenAddress,
          },
        })
      })

      const rewardTotals = await Promise.all(rewardTotalsPromises).then((res) => res.map((r) => r.data))

      const weekTimestampsPromises = finishedWeeks.map(({ rewardTokenAddress }) => {
        return merkl.campaigns.index.get({
          query: {
            chainId,
            items: 2,
            tokenAddress: rewardTokenAddress,
            withOpportunity: false,
            test: true,
          },
        })
      })

      const weekTimestamps = await Promise.all(weekTimestampsPromises).then((res) => res.map((r) => r.data))

      return {
        userRewards: userRewards.data?.[0]?.rewards,
        rewardTotals: rewardTotals.map((r, i) => ({
          ...r,
          startTimestamp: weekTimestamps[i]?.[1]?.startTimestamp,
          endTimestamp: weekTimestamps[i]?.[1]?.endTimestamp,
        })),
      }
    },
    select: ({ userRewards, rewardTotals }) => {
      const pointsToken = userRewards?.find((reward) => reward.token.address === POINTS_TOKEN_ADDRESS)

      const finishedWeeksPoints = finishedWeeks.flatMap(({ rewardTokenAddress, campaignId }, index) => {
        const rewardTotal = rewardTotals[index]
        const userPoints = pointsToken?.breakdowns.find((breakdown) => breakdown.campaignId === campaignId)
        const pointsAmount =
          typeof userPoints?.amount === 'bigint' ? userPoints.amount : BigInt(userPoints?.amount ?? 0)

        if (!rewardTotal || !rewardTotal.startTimestamp || !rewardTotal.endTimestamp || !rewardTotal.amount) {
          return []
        }

        if (!campaignId || !pointsAmount) {
          return {
            rewardTokenAddress,
            userPointsCalculated: undefined,
            startTimestamp: rewardTotal.startTimestamp,
            endTimestamp: rewardTotal.endTimestamp,
          }
        }

        return {
          rewardTokenAddress,
          userPointsCalculated: NormalizedUnitNumber(formatUnits(pointsAmount, 18)),
          startTimestamp: rewardTotal.startTimestamp,
          endTimestamp: rewardTotal.endTimestamp,
        }
      })

      if (!pointsToken) {
        return {
          amount: NormalizedUnitNumber(0),
          formattedAmount: formatter.format(0),
          finishedWeeksPoints,
        }
      }

      return {
        ...formatPointsAmount({
          amount: typeof pointsToken.amount === 'bigint' ? pointsToken.amount.toString() : pointsToken.amount,
          decimals: pointsToken.token.decimals,
        }),
        finishedWeeksPoints,
      }
    },
    enabled: !!account,
  })
}

export function userPointsQueryKey(account: Address): unknown[] {
  return ['merkl', 'user-points', account]
}
